import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import ArrowIcon from "../../icons/ArrowIcon";
import DateInputGradient from "../../atoms/DateInputGradient/DateInputGradient";
import { periods } from "./statisticsConfig";
import { formatValue } from "./statisticsUtils";

interface ExpensesSectionProps {
    stats: any;
    linkTo: string;
    colorConfig: { textColor: string; color: string };
    expensesDateFrom: string;
    expensesDateTo: string;
    setExpensesDateFrom: (v: string) => void;
    setExpensesDateTo: (v: string) => void;
    onReset: () => void;
}

const ExpensesSection: FC<ExpensesSectionProps> = ({
                                                       stats,
                                                       linkTo,
                                                       colorConfig,
                                                       expensesDateFrom,
                                                       expensesDateTo,
                                                       setExpensesDateFrom,
                                                       setExpensesDateTo,
                                                       onReset
                                                   }) => {

    // 1. Логика периодов (Grid vs All Time)
    const { mainPeriods, allTimePeriod } = useMemo(() => {
        const norm = (v: any) => String(v ?? "").toLowerCase();

        const all =
            periods.find((p: any) => {
                const k = norm(p.key);
                const l = norm(p.label);
                return (
                    ["all", "all_time", "alltime", "total", "lifetime"].includes(k) ||
                    /все|всё|all\s*time|lifetime/i.test(l)
                );
            }) || null;

        const pick = (rx: RegExp, keyFallback?: string) => {
            const byKey = keyFallback ? periods.find((p: any) => norm(p.key) === norm(keyFallback)) : null;
            if (byKey && (!all || byKey.key !== all.key)) return byKey;

            const byLabel = periods.find((p: any) => rx.test(norm(p.label)) && (!all || p.key !== all.key));
            return byLabel || null;
        };

        const desired = [pick(/день|day/i, "day"), pick(/недел|week/i, "week"), pick(/месяц|month/i, "month"), pick(/год|year/i, "year")].filter(
            Boolean
        ) as any[];

        const dedup = Array.from(new Map(desired.map((p: any) => [String(p.key), p])).values());
        const fallbackMain = periods.filter((p: any) => (!all || p.key !== all.key));

        return {
            mainPeriods: dedup.length ? dedup : fallbackMain,
            allTimePeriod: all
        };
    }, []);

    const renderExpensesItemsDefault = () => {
        const overall = stats?.expenses?.overall;
        if (!overall) return null;

        return (
            <div className="statistics-items__item">
                <Link to={linkTo} className="statistics-items__item-link">
                    <ArrowIcon />
                </Link>
                <div className="statistics-items__item-title">Расходы: сумма в USDT</div>
                <div className="statistics-items__item-list">
                    <div className="statistics-items__item-content">

                        {/* Выстраивание в колонку */}
                        <div className="statistics-items__item-content-counter statistics-items__item-content-counter--column">

                            <div className="statistics-items__item-content-counter-main">
                                <span>{formatValue(overall.day?.rub_amount, true, 'USDT')}</span>
                                <span>
                                    <b>за сегодня →</b> общий объём
                                </span>
                            </div>

                            <div className="statistics-items__item-content-counter-total statistics-items__item-content-counter-total--row">
                                <div className="statistics-items__periods">
                                    <div className="statistics-items__periods-grid">
                                        {mainPeriods.map((period: any) => {
                                            const data = overall[period.key];
                                            const value = data?.rub_amount || 0;
                                            return (
                                                <div
                                                    key={`expenses-${period.key}`}
                                                    className="is-red" // Красный цвет для расходов
                                                >
                                                    {formatValue(value, true, 'USDT')}
                                                    <span>{period.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {allTimePeriod && (
                                        <div className="statistics-items__periods-all">
                                            {(() => {
                                                const data = overall[allTimePeriod.key];
                                                const value = data?.rub_amount || 0;
                                                return (
                                                    <div
                                                        key={`expenses-${allTimePeriod.key}`}
                                                        className="is-red"
                                                    >
                                                        {formatValue(value, true, 'USDT')}
                                                        <span>{allTimePeriod.label}</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderExpensesItemsRange = () => {
        const overall = stats?.expenses?.range?.overall;
        if (!overall) return null;

        const label = expensesDateFrom || expensesDateTo ? `${expensesDateFrom || "…"} — ${expensesDateTo || "…"}` : "…";
        const v = Number(overall?.rub_amount || 0);

        return (
            <div className="statistics-items__item">
                <Link to={linkTo} className="statistics-items__item-link">
                    <ArrowIcon />
                </Link>
                <div className="statistics-items__item-title">Расходы: сумма в рублях (за период)</div>
                <div className="statistics-items__item-list">
                    <div className="statistics-items__item-content">
                        <div className="statistics-items__item-content-counter">
                            <div className="statistics-items__item-content-counter-main">
                                <span>{formatValue(v, true)}</span>
                                <span>
                                    <b>период →</b> {label}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const hasExpensesRange = !!(expensesDateFrom || expensesDateTo) && !!stats?.expenses?.range;

    return (
        <>
            <div className="statistics-items__filter">
                <div className="statistics-items__filter-row">
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="expensesDateFrom"
                            placeholder="Дата с"
                            value={expensesDateFrom}
                            onChange={v => setExpensesDateFrom(v)}
                        />
                    </div>
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="expensesDateTo"
                            placeholder="Дата по"
                            value={expensesDateTo}
                            onChange={v => setExpensesDateTo(v)}
                        />
                    </div>
                    <button type="button" className="statistics-items__transfer-tab" onClick={onReset}>
                        Сбросить
                    </button>
                </div>
            </div>

            <div className="statistics-items__list">{hasExpensesRange ? renderExpensesItemsRange() : renderExpensesItemsDefault()}</div>
        </>
    );
};

export default ExpensesSection;